function normalizeMenuPath(path: string): string {
  return path.endsWith('/') ? path : `${path}/`
}

/** Slug в URL → канонический link пункта меню врача (с trailing slash). */
export const DOCTOR_PREVIEW_SLUG_TO_MENU_LINK: Record<string, string> = {
  schedule: '/profile-doctor/schedule/',
  history: '/profile-doctor/history/',
  finances: '/profile-doctor/finances/',
  statistics: '/profile-doctor/statistics/',
  reviews: '/profile-doctor/reviews/',
  'partner-programs': '/profile-doctor/partner-programs/',
  'referral-program': '/profile-doctor/referral-program/',
  education: '/profile-doctor/education/',
}

/** Slug в URL → канонический link пункта меню клиники. */
export const CLINIC_PREVIEW_SLUG_TO_MENU_LINK: Record<string, string> = {
  doctors: '/profile-clinic/doctors/',
  'doctors-schedule': '/profile-clinic/doctors/schedule/',
  'doctors-history': '/profile-clinic/online-consultations/',
  services: '/profile-clinic/services/',
  finances: '/profile-clinic/finances/',
  statistics: '/profile-clinic/statistics/',
  'partner-programs': '/profile-clinic/partner-programs/',
  widget: '/profile-clinic/widget/',
  education: '/profile-clinic/education/',
}

function invertMap(m: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [slug, link] of Object.entries(m)) {
    out[normalizeMenuPath(link)] = slug
  }
  return out
}

const DOCTOR_MENU_LINK_TO_PREVIEW_SLUG = invertMap(DOCTOR_PREVIEW_SLUG_TO_MENU_LINK)
const CLINIC_MENU_LINK_TO_PREVIEW_SLUG = invertMap(CLINIC_PREVIEW_SLUG_TO_MENU_LINK)

export function isDoctorPreviewSlug(slug: string): boolean {
  return Boolean(slug && DOCTOR_PREVIEW_SLUG_TO_MENU_LINK[slug])
}

export function isClinicPreviewSlug(slug: string): boolean {
  return Boolean(slug && CLINIC_PREVIEW_SLUG_TO_MENU_LINK[slug])
}

export function getDoctorSectionPreviewPath(menuLink: string): string | null {
  const slug = DOCTOR_MENU_LINK_TO_PREVIEW_SLUG[normalizeMenuPath(menuLink)]
  if (!slug) return null
  return `/profile-doctor/preview/${slug}/`
}

export function getClinicSectionPreviewPath(menuLink: string): string | null {
  const slug = CLINIC_MENU_LINK_TO_PREVIEW_SLUG[normalizeMenuPath(menuLink)]
  if (!slug) return null
  return `/profile-clinic/preview/${slug}/`
}

export type PersonalPreviewRole = 'doctor' | 'clinic'

export function getPersonalSectionPreviewPath(role: PersonalPreviewRole, menuLink: string): string | null {
  if (role === 'doctor') return getDoctorSectionPreviewPath(menuLink)
  return getClinicSectionPreviewPath(menuLink)
}

const DOCTOR_PREVIEW_PATH = /^\/profile-doctor\/preview\/([^/]+)\/?/

export function parseDoctorPreviewSlug(path: string): string | null {
  const m = path.match(DOCTOR_PREVIEW_PATH)
  return m?.[1] ?? null
}

const CLINIC_PREVIEW_PATH = /^\/profile-clinic\/preview\/([^/]+)\/?/

export function parseClinicPreviewSlug(path: string): string | null {
  const m = path.match(CLINIC_PREVIEW_PATH)
  return m?.[1] ?? null
}

export function getDoctorMenuLinkFromPreviewSlug(slug: string): string | null {
  return DOCTOR_PREVIEW_SLUG_TO_MENU_LINK[slug] ?? null
}

export function getClinicMenuLinkFromPreviewSlug(slug: string): string | null {
  return CLINIC_PREVIEW_SLUG_TO_MENU_LINK[slug] ?? null
}

export function isDoctorPreviewPathActiveForMenuLink(routePath: string, menuLink: string): boolean {
  const slug = parseDoctorPreviewSlug(routePath)
  if (!slug) return false
  const resolved = getDoctorMenuLinkFromPreviewSlug(slug)
  if (!resolved) return false
  return normalizeMenuPath(resolved) === normalizeMenuPath(menuLink)
}

export function isClinicPreviewPathActiveForMenuLink(routePath: string, menuLink: string): boolean {
  const slug = parseClinicPreviewSlug(routePath)
  if (!slug) return false
  const resolved = getClinicMenuLinkFromPreviewSlug(slug)
  if (!resolved) return false
  return normalizeMenuPath(resolved) === normalizeMenuPath(menuLink)
}
